import React, { useEffect, useState, useCallback } from 'react';
import { host_url } from '../../../App';
import BackArrow from '../../../BackArrow';
import '../../../styles/CiTable.css';
import './Antraege.css';
import Cookies from 'js-cookie';

const STATUS_LABELS = {
  'eingegangen': 'Eingegangen',
  'in Bearbeitung': 'In Bearbeitung',
  'erledigt': 'Erledigt',
};

const STATUS_CLASS = {
  'eingegangen': 'status-eingegangen',
  'in Bearbeitung': 'status-in-bearbeitung',
  'erledigt': 'status-erledigt',
};

// Einfache Ähnlichkeitsprüfung (Levenshtein-Distanz normalisiert)
function aehnlichkeit(a, b) {
  const s1 = a.toLowerCase().trim();
  const s2 = b.toLowerCase().trim();
  if (s1 === s2) return 1;
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  if (longer.length === 0) return 1;
  const editDistance = (a, b) => {
    const m = a.length, n = b.length;
    const dp = Array.from({ length: m + 1 }, (_, i) =>
      Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    );
    for (let i = 1; i <= m; i++)
      for (let j = 1; j <= n; j++)
        dp[i][j] = a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    return dp[m][n];
  };
  return (longer.length - editDistance(longer, shorter)) / longer.length;
}

function istDuplikat(antrag, allAntraege) {
  return allAntraege.some(
    (a) => a.ID !== antrag.ID && aehnlichkeit(a.Antrag, antrag.Antrag) > 0.8
  );
}

function isParlament() {
  return (
    Cookies.get('Bereich') === 'Parlament' &&
    Cookies.get('ppasswordentered') === 'true'
  );
}

function Antraege() {
  const [antraege, setAntraege] = useState([]);
  const [ausschuesse, setAusschuesse] = useState([]);
  const [filterAusschuss, setFilterAusschuss] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const parlament = isParlament();

  const ladeAntraege = useCallback(() => {
    setLoading(true);
    fetch(`${host_url}/pabrika`)
      .then((r) => r.json())
      .then((data) => {
        setAntraege(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Fehler beim Laden der Anträge.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    ladeAntraege();
    fetch(`${host_url}/ausschuesse`)
      .then((r) => r.json())
      .then(setAusschuesse)
      .catch(() => {});
  }, [ladeAntraege]);

  const startEdit = (antrag) => {
    setEditId(antrag.ID);
    setEditDraft({
      Status: antrag.Status || 'eingegangen',
      Kommentar: antrag.Kommentar || '',
      AusschussID: antrag.AusschussID || '',
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditDraft({});
  };

  const saveEdit = (id) => {
    setSaving(true);
    fetch(`${host_url}/pabrika/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Status: editDraft.Status,
        Kommentar: editDraft.Kommentar,
        AusschussID: editDraft.AusschussID || null,
      }),
    })
      .then((r) => r.json())
      .then(() => {
        setEditId(null);
        setEditDraft({});
        ladeAntraege();
      })
      .catch(() => setError('Fehler beim Speichern.'))
      .finally(() => setSaving(false));
  };

  const deleteAntrag = (id, name) => {
    if (!window.confirm(`Antrag von „${name}" wirklich löschen?`)) return;
    fetch(`${host_url}/pabrika/${id}`, { method: 'DELETE' })
      .then(() => ladeAntraege())
      .catch(() => setError('Fehler beim Löschen.'));
  };

  const gefilterteAntraege = antraege.filter((a) => {
    if (filterAusschuss && String(a.AusschussID) !== filterAusschuss) return false;
    if (filterStatus && a.Status !== filterStatus) return false;
    return true;
  });

  const formatDatum = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="antraege-page">
      <div className="page-header">
        <BackArrow to="/parlament" />
        <h1>Anträge</h1>
      </div>

      {error && <div className="antraege-error">{error}</div>}

      {/* Filter-Leiste */}
      <div className="antraege-filter">
        <div className="dropdown-wrapper">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">Alle Status</option>
            <option value="eingegangen">Eingegangen</option>
            <option value="in Bearbeitung">In Bearbeitung</option>
            <option value="erledigt">Erledigt</option>
          </select>
        </div>
        <div className="dropdown-wrapper">
          <select value={filterAusschuss} onChange={(e) => setFilterAusschuss(e.target.value)}>
            <option value="">Alle Ausschüsse</option>
            <option value="null">Kein Ausschuss</option>
            {ausschuesse.map((a) => (
              <option key={a.AusschussID} value={String(a.AusschussID)}>
                {a.Name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="antraege-loading">Lade Anträge…</p>
      ) : gefilterteAntraege.length === 0 ? (
        <p className="antraege-leer">Keine Anträge vorhanden.</p>
      ) : (
        <div className="antraege-liste">
          {gefilterteAntraege.map((antrag) => {
            const duplikat = parlament && istDuplikat(antrag, antraege);
            const istEdit = editId === antrag.ID;
            const ausschussName = ausschuesse.find(
              (a) => a.AusschussID === antrag.AusschussID
            )?.Name;

            return (
              <div
                key={antrag.ID}
                className={`antrag-card ${STATUS_CLASS[antrag.Status] || ''} ${duplikat ? 'duplikat' : ''}`}
              >
                {duplikat && (
                  <div className="duplikat-badge">
                    <span className="material-symbols-outlined">content_copy</span>
                    Mögliches Duplikat
                  </div>
                )}

                <div className="antrag-header">
                  <div className="antrag-meta">
                    <span className="antrag-name">{antrag.Name}</span>
                    {antrag['Zelt/Bereich'] && (
                      <span className="antrag-bereich">{antrag['Zelt/Bereich']}</span>
                    )}
                  </div>
                  <span className={`status-badge ${STATUS_CLASS[antrag.Status] || ''}`}>
                    {antrag.Status === 'erledigt' && (
                      <span className="material-symbols-outlined status-icon">check_circle</span>
                    )}
                    {STATUS_LABELS[antrag.Status] || antrag.Status}
                  </span>
                </div>

                <p className="antrag-text">{antrag.Antrag}</p>

                <div className="antrag-footer">
                  {ausschussName && (
                    <span className="antrag-ausschuss">
                      <span className="material-symbols-outlined">groups</span>
                      {ausschussName}
                    </span>
                  )}
                  {antrag.Status === 'erledigt' && antrag.ErledgtDatum && (
                    <span className="antrag-datum">
                      <span className="material-symbols-outlined">event_available</span>
                      Erledigt am {formatDatum(antrag.ErledgtDatum)}
                    </span>
                  )}
                </div>

                {antrag.Kommentar && !istEdit && (
                  <div className="antrag-kommentar">
                    <span className="material-symbols-outlined">comment</span>
                    <span>{antrag.Kommentar}</span>
                  </div>
                )}

                {/* Parlament-interne Bearbeitung */}
                {parlament && !istEdit && (
                  <div className="antrag-actions">
                    <button className="btn-edit" onClick={() => startEdit(antrag)}>
                      <span className="material-symbols-outlined">edit</span>
                      Bearbeiten
                    </button>
                    <button className="btn-danger btn-delete" onClick={() => deleteAntrag(antrag.ID, antrag.Name)}>
                      <span className="material-symbols-outlined">delete</span>
                      Löschen
                    </button>
                  </div>
                )}

                {parlament && istEdit && (
                  <div className="antrag-edit-form">
                    <label className="freitext-label">
                      Status
                      <div className="dropdown-wrapper" style={{ marginTop: '4px' }}>
                        <select
                          value={editDraft.Status}
                          onChange={(e) => setEditDraft({ ...editDraft, Status: e.target.value })}
                        >
                          <option value="eingegangen">Eingegangen</option>
                          <option value="in Bearbeitung">In Bearbeitung</option>
                          <option value="erledigt">Erledigt</option>
                        </select>
                      </div>
                    </label>

                    <label className="freitext-label">
                      Ausschuss
                      <div className="dropdown-wrapper" style={{ marginTop: '4px' }}>
                        <select
                          value={editDraft.AusschussID}
                          onChange={(e) => setEditDraft({ ...editDraft, AusschussID: e.target.value })}
                        >
                          <option value="">Kein Ausschuss</option>
                          {ausschuesse.map((a) => (
                            <option key={a.AusschussID} value={a.AusschussID}>
                              {a.Name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </label>

                    <label className="freitext-label">
                      Kommentar / Begründung
                      <textarea
                        className="freitext-input"
                        value={editDraft.Kommentar}
                        onChange={(e) => setEditDraft({ ...editDraft, Kommentar: e.target.value })}
                        placeholder="Entscheidung oder Begründung eintragen…"
                        rows={3}
                      />
                    </label>

                    <div className="antrag-edit-buttons">
                      <button onClick={() => saveEdit(antrag.ID)} disabled={saving}>
                        {saving ? 'Speichern…' : 'Speichern'}
                      </button>
                      <button className="btn-secondary" onClick={cancelEdit}>
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Antraege;
